import {
  CALLOUT_BLOCK_MARKER_CLASS,
  getCalloutBulletComponents,
  getCalloutDensity,
  mermaidConfig,
  reconstructNestedMarkdown,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  type CalloutDensity,
  type CalloutVariant,
} from '@heidi/answer-kit';
import { LightBulb } from '@heidi/design-system/icons';
import { cn } from '@heidi/utils';
import { useMemo, type ComponentType, type JSX, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Streamdown } from 'streamdown';

interface KeyPointsBlockProps {
  children?: ReactNode;
  className?: string;
  isStreaming?: boolean;
  /** `<cite-badge>` renderer supplied by the surface that owns the citation map. */
  citeBadgeComponent?: ComponentType<{ 'data-idx'?: string }>;
  /** Variant to use - 'in-session' is smaller/compact, 'out-of-session' is larger/spacious */
  variant?: CalloutVariant;
  /**
   * The hosting surface's type density, which sets the bullet type scale. Pass
   * this whenever the surface knows its own prose size; `variant` is a guess
   * and gets it wrong wherever "no scribe session" does not mean "16px prose".
   */
  density?: CalloutDensity;
}

/** External-link handler for prose inside the Key Points card. Lives at module
 *  scope (not inside the component) so the factory result stays referentially
 *  stable across renders when `linkComponent` is the only reason to rebuild
 *  the components map. */
const KeyPointsLink = ({ children, href }: { children?: ReactNode; href?: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-text-link underline hover:text-text-link-hover"
  >
    {children}
  </a>
);

/**
 * KeyPointsBlock - A styled container for key points in Evidence mode responses.
 *
 * The LLM emits content wrapped in `<keypoints>` tags. The outer Streamdown +
 * rehype-raw parses the tag into this component; the children (markdown) are
 * re-parsed by a nested Streamdown so bullets render correctly — HTML blocks
 * don't process markdown by default.
 *
 * NOTE: The element name must NOT contain hyphens. Streamdown's block parser
 * uses `\w+` regex which doesn't match hyphens, causing content to be split
 * incorrectly across blocks (EVI-509).
 *
 * @example LLM Output:
 * ```
 * <keypoints>
 * - First key point about the topic
 * - Second key point with more details
 * </keypoints>
 * ```
 */
export const KeyPointsBlock = ({
  children,
  className,
  isStreaming = false,
  citeBadgeComponent,
  variant = 'in-session',
  density: densityProp,
}: KeyPointsBlockProps): JSX.Element | null => {
  const density = densityProp ?? getCalloutDensity(variant);
  const isOutOfSession = variant === 'out-of-session';
  // Re-parse children as markdown for the nested Streamdown, preserving inline
  // cite-badge markers (shared with SafetyCallout — see the helper for why).
  const textContent = useMemo(() => reconstructNestedMarkdown(children), [children]);

  const components = useMemo(
    () =>
      getCalloutBulletComponents({
        density,
        linkComponent: KeyPointsLink,
        citeBadgeComponent,
      }),
    [citeBadgeComponent, density]
  );

  if (!textContent) {
    return null;
  }

  return (
    <div
      data-testid="key-points-block"
      className={cn(
        // Shared marker class — see callout-helpers.ts. Drives all OOS
        // override rules that scope to callouts (`[&_.callout-block_*]`),
        // keeping KeyPoints and KeySafetyNotes in lockstep.
        CALLOUT_BLOCK_MARKER_CLASS,
        // `bg-validation-info-muted` is the semantic "info muted background"
        // token — resolves correctly across all four themes.
        'flex flex-col rounded-xl bg-validation-info-muted',
        isOutOfSession ? 'gap-4 p-6' : 'gap-3 px-4 py-5',
        'my-4 first:mt-0',
        // Prevent overflow on mobile - min-w-0 allows flex child to shrink below content size
        'min-w-0 overflow-hidden',
        className
      )}
    >
      <div className="flex items-center gap-1">
        <LightBulb
          size={isOutOfSession ? 20 : 18}
          className="text-skies-950 newBrandDark:text-text-primary"
        />
        <span
          className={cn(
            'font-exposure font-medium tracking-tight text-skies-950 newBrandDark:text-text-primary',
            isOutOfSession ? 'text-xl' : 'text-base'
          )}
        >
          <FormattedMessage
            defaultMessage="Key points"
            id="XFgyDdkbm0"
            description="Heading for the key-points summary card on Evidence answers"
          />
        </span>
      </div>

      {/* Accent ink color is set on this wrapper so it cascades to the
          callout's <li> / <p> / <strong> / <em> via inheritance — the shared
          factory deliberately doesn't set text colors so the same handlers
          work for both KeyPoints (blue ink) and KeySafetyNotes (neutral ink).
          `text-skies-950` is a primitive — no semantic "info ink" token
          exists, and the blue ink is intentional visual identity. */}
      <div className="text-skies-950 newBrandDark:text-text-primary">
        <Streamdown
          components={components}
          isAnimating={isStreaming}
          codeBlockMaxHeight={0}
          tableMaxHeight={0}
          plugins={streamdownPlugins}
          mermaid={mermaidConfig}
          shikiTheme={shikiTheme}
          rehypePlugins={streamdownRehypePlugins}
        >
          {textContent}
        </Streamdown>
      </div>
    </div>
  );
};

export default KeyPointsBlock;
